import { Edge, Node } from 'reactflow';
import { getCollapsedState } from '@components/MainStage/collapsedLayers.store';
import useStore from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import {
  CiaSubLayers,
  ConceptualSubLayerTypes,
  ContextualSubLayerTypes,
  InitiativesSubLayerTypes,
  SubLayersWithoutValidStatus,
  SubLayerTypes,
} from '@constants/canvas/layers';
import { filtersSubject } from '@context/ProjectFilters/FiltersSubject';
import useQuestionnaireResultStore from '@store/zustand/questionnaire-result';
import { isCriteriaFieldsMatch } from '@utils/canvasHelpers';
import { checkScopeFilter } from '@utils/domains';

class CanvasMap {
  nodes: Node[];

  relations: Edge[];

  nodeMap: Map<string, Node>;

  relationMap: Map<string, Edge>;

  constructor() {
    const { nodes, edges } = useStore.getState();

    this.nodes = nodes;
    this.relations = edges;
    this.nodeMap = new Map(nodes.map((node) => [node.id, node]));
    this.relationMap = new Map(edges.map((edge) => [edge.id, edge]));

    this.subscribeToCanvas();
  }

  private subscribeToCanvas() {
    useStore.subscribe((state) => {
      this.nodes = state.nodes;
      this.relations = state.edges;

      this.nodeMap = new Map(state.nodes.map((node) => [node.id, node]));
      this.relationMap = new Map(state.edges.map((edge) => [edge.id, edge]));
    });
  }

  isNodeHiddenByFilters(id: string) {
    const {
      domains,
      reviewStatus,
      validationStatus,
      cia,
      operationalStatuses,
      operatingModelStatuses,
      partners,
    } = filtersSubject.getValue();

    const { result: questionnaireResult } =
      useQuestionnaireResultStore.getState();

    const node = this.nodeMap.get(id);
    if (!node) return false;

    const isContextualOrConceptual = [
      ContextualSubLayerTypes.StrategicEnablers,
      ContextualSubLayerTypes.StrategicPriorities,
      ConceptualSubLayerTypes.BusinessAttributes,
      ConceptualSubLayerTypes.SecurityAttributes,
    ].includes(node.parentNode as ContextualSubLayerTypes);

    if (domains.length) {
      const isCustomNode = node.type === NodesTypes.CustomNode;

      const isDomainIncluded = checkScopeFilter(
        domains,
        node.data?.dto?.type,
        node.data?.dto?.domainNodes,
      );

      return isCustomNode && !isDomainIncluded;
    }

    if (reviewStatus.length) {
      return !(
        node.type !== NodesTypes.CustomNode || node.data?.dto?.review_required
      );
    }

    if (validationStatus.length) {
      const nodeWithoutValidStatus = SubLayersWithoutValidStatus.includes(
        node.parentNode as SubLayerTypes,
      );

      const isVisible =
        node.type !== NodesTypes.CustomNode ||
        validationStatus.includes(node.data?.dto?.valid_status);

      return nodeWithoutValidStatus || !isVisible;
    }

    if (
      Object.values(cia).some((fields) =>
        Object.values(fields).some((answers) => answers?.length),
      )
    ) {
      if (node.type !== NodesTypes.CustomNode) return false;

      if (questionnaireResult) {
        const isCiaSublayer = CiaSubLayers.includes(node.parentNode as any);

        if (!isCiaSublayer) return node;
      }

      const isAssuranceMatch = isCriteriaFieldsMatch(
        cia.assurance_level,
        node.data.dto.cia?.assurance_level?.criterias,
      );

      const isCoverageMatch = isCriteriaFieldsMatch(
        cia.coverage,
        node.data.dto.cia?.coverage?.criterias,
        { passIfNodeFieldEmpty: Boolean(questionnaireResult) },
      );

      const isApplicabilityMatch = isCriteriaFieldsMatch(
        cia.applicability,
        node.data.dto.cia?.applicability?.criterias,
        { passIfNodeFieldEmpty: Boolean(questionnaireResult) },
      );

      const isAllMatch =
        isAssuranceMatch && isCoverageMatch && isApplicabilityMatch;

      return !isAllMatch;
    }

    if (operationalStatuses.length) {
      if (isContextualOrConceptual) {
        return node.data.canvas.hiddenBA;
      }

      if (node.type !== NodesTypes.CustomNode) return false;

      if (questionnaireResult) {
        if (node.parentNode === InitiativesSubLayerTypes.ChangeInitiatives) {
          return false;
        }
      }

      const isStatusMatch = operationalStatuses.includes(
        node.data.dto?.meta_data?.operational_status,
      );

      return !isStatusMatch;
    }

    if (operatingModelStatuses.length) {
      if (node.type !== NodesTypes.CustomNode) return false;

      const isStatusMatch = operatingModelStatuses.includes(
        node.data.dto?.meta_data?.operational_model,
      );

      return !isStatusMatch;
    }

    if (partners.length) {
      if (node.type !== NodesTypes.CustomNode) return false;

      const isPartnerMatch = partners.some(
        (partner) => node.data.dto.partners?.includes(partner),
      );

      return !isPartnerMatch;
    }

    return false;
  }

  /*
    If the above / bottom layer is collapsed - iterate relations and find all related nodes from the closest non-collapsed layer
  */
  async findRelatedNodes(
    relatedIds: string[],
    key: 'parentsIds' | 'childrenIds',
  ): Promise<string[]> {
    const { isInvisibleCollapsedSublayer } = getCollapsedState();
    const relatedNodesIds: string[] = [];

    for (const id of relatedIds) {
      const node = this.nodeMap.get(id);

      // eslint-disable-next-line no-continue
      if (!node) continue;

      const subLayerId = node.parentNode;

      // eslint-disable-next-line no-continue
      if (!subLayerId) continue;

      if (isInvisibleCollapsedSublayer(subLayerId as SubLayerTypes)) {
        // eslint-disable-next-line no-await-in-loop
        const stepParentNodesIds = await this.findRelatedNodes(
          node.data.dto[key],
          key,
        );

        relatedNodesIds.push(...stepParentNodesIds);
      } else {
        relatedNodesIds.push(id);
      }
    }

    return relatedNodesIds;
  }

  async focusCurrentNode(id: string) {
    const node = this.nodeMap.get(id);

    if (!node) return;

    const { parentsIds, childrenIds } = node.data.dto;

    const closestParentIds = await this.findRelatedNodes(
      parentsIds,
      'parentsIds',
    );

    const closestChildrenIds = await this.findRelatedNodes(
      childrenIds,
      'childrenIds',
    );

    const newNodes = this.nodes.map((node) => {
      const isNodeRelated =
        node.id === id ||
        closestParentIds.includes(node.id) ||
        closestChildrenIds.includes(node.id);

      return {
        ...node,
        data: {
          ...node.data,
          canvas: {
            ...node.data.canvas,
            disabled: !isNodeRelated,
            preventSelection: !isNodeRelated,
          },
        },
      };
    });

    useStore.setState({ nodes: newNodes });
  }

  async resetFocusedNode() {
    const newNodes = this.nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        canvas: {
          ...node.data.canvas,
          disabled: false,
          preventSelection: false,
        },
      },
    }));

    useStore.setState({ nodes: newNodes });
  }
}

export const canvasMap = new CanvasMap();
